import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, TOKEN_KEY, setAuthFailureHandler } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const skipRefreshRef = useRef(false);

  const logout = useCallback(
    (redirectTo = "/") => {
      // Atomically clear local state BEFORE navigating so that ProtectedRoute,
      // which already mounted with user=truthy, never re-renders at a guarded
      // path with user=null (that races into a /login bounce).
      skipRefreshRef.current = true;
      window.localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      api.post("/auth/logout").catch(() => undefined);
      // Use react-router navigate to avoid a full-page reload flash.
      navigate(redirectTo || "/", { replace: true });
    },
    [navigate],
  );

  // Hook the api interceptor's auto-logout to our state
  useEffect(() => {
    setAuthFailureHandler(() => logout("/login"));
    return () => setAuthFailureHandler(null);
  }, [logout]);

  const hydrate = useCallback(async () => {
    if (skipRefreshRef.current) {
      // Just logged out — don't re-fetch /me. Will reset on next mount.
      skipRefreshRef.current = false;
      setLoading(false);
      return;
    }
    const token = window.localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch (_) {
      window.localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const login = useCallback((token, nextUser) => {
    window.localStorage.setItem(TOKEN_KEY, token);
    setUser(nextUser);
  }, []);

  const updateUser = useCallback((nextUser) => {
    setUser(nextUser);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, updateUser, refresh: hydrate }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};

export default AuthContext;
